import type { InfoCard } from '../workspace.types';

/**
 * Sample information card for the dev / quick-start experience — mirrors the
 * shape a live agent sends over `present_info`. NOT shipped product copy; it
 * lets the info feed be exercised without a backend.
 */

let seq = 0;

export const sampleOverviewCard = (): InfoCard => ({
  kind: 'generic',
  id: `demo-overview-${Date.now()}-${seq++}`,
  title: 'Patient overview — Priya Nair',
  subtitle: '47F · Type 2 diabetes, iron deficiency under investigation',
  blocks: [
    {
      type: 'facts',
      title: 'Latest results',
      facts: [
        { label: 'HbA1c', value: '6.8%', hint: 'Mar 2026', tone: 'positive' },
        { label: 'Ferritin', value: '11 µg/L', hint: 'Ref 30–300', tone: 'critical' },
        { label: 'Haemoglobin', value: '118 g/L', hint: 'Ref 115–165', tone: 'warning' },
        { label: 'Blood pressure', value: '128/78', hint: 'This visit' },
        { label: 'eGFR', value: '>90', hint: 'Feb 2026', tone: 'positive' },
        { label: 'BMI', value: '27.4', hint: 'This visit' },
      ],
    },
    {
      type: 'chart',
      title: 'HbA1c trend',
      chartKind: 'line',
      unit: '%',
      series: [
        { label: 'Mar 24', value: 8.1 },
        { label: 'Sep 24', value: 7.6 },
        { label: 'Mar 25', value: 7.2 },
        { label: 'Sep 25', value: 7.0 },
        { label: 'Mar 26', value: 6.8 },
      ],
      referenceHigh: 7.0,
      referenceLabel: 'Target ≤ 7%',
    },
    {
      type: 'list',
      title: 'Active medications',
      items: [
        { text: 'Metformin 1 g BD', detail: 'Since 2022 · well tolerated' },
        { text: 'Perindopril 5 mg daily', detail: 'BP at target' },
      ],
    },
    {
      type: 'timeline',
      title: 'Recent history',
      events: [
        { date: 'Nov 25', text: 'Reported fatigue and reduced exercise tolerance' },
        {
          date: 'Feb 26',
          text: 'Iron studies — ferritin 11 µg/L',
          detail: 'No overt bleeding reported',
        },
        { date: 'Mar 26', text: 'Referred to endocrinology for iron deficiency workup' },
      ],
    },
    {
      type: 'details',
      title: 'Diabetes management detail',
      blocks: [
        {
          type: 'text',
          body: 'Diet-led improvement over 24 months with stable metformin dosing. No hypoglycaemic episodes reported. Annual retinal screening up to date; last foot check normal.',
        },
        {
          type: 'table',
          columns: ['Check', 'Last done', 'Result'],
          rows: [
            ['Retinal screen', 'Oct 2025', 'No retinopathy'],
            ['Foot check', 'Oct 2025', 'Normal'],
            ['Urine ACR', 'Feb 2026', '1.1 mg/mmol'],
          ],
        },
      ],
    },
  ],
  followUps: [
    'Show her iron studies in detail',
    'What did the last consult cover?',
    'Compare her lipids over time',
  ],
  ts: Date.now(),
});
